import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Row, Col, Typography, Flex, Segmented, Button, Upload, Alert, Tag, message, Divider } from "antd";
import { UserAddOutlined, FileExcelOutlined, CheckCircleOutlined, UpOutlined, DownOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { getCookie } from "../../../utils/cookieUtils";
import { getEmployees } from "../../../services/employeeService";
import { AVAILABLE_STATUSES } from "./leadsData";

const { Title, Text } = Typography;

const resolveStatusList = (reduxCallStatus, availableStatuses) => {
  let list = AVAILABLE_STATUSES;
  if (Array.isArray(reduxCallStatus) && reduxCallStatus.length > 0) {
    list = reduxCallStatus;
  } else if (Array.isArray(availableStatuses) && availableStatuses.length > 0) {
    list = availableStatuses;
  }
  return list.filter((st) => {
    const norm = (st || "").toLowerCase();
    return norm !== "closed lost" && norm !== "converted";
  });
};

function AddLeadModal({ visible, onClose, onAddLead, onImportLeads, companies, availableStatuses, companyCode, employeeId, employee, employees: propEmployees }) {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const reduxCallStatus = useSelector((state) => state.executor?.companyInfo?.callStatus);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = companyCode || reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";
  const defaultEmpId = employeeId || employee?.id || employee?._id || employee?.employeeId || user?.id || user?._id || user?.employeeId || getCookie("userId") || getCookie("employeeId");

  const role = user?.role || getCookie("role") || "";
  const isEmployee = role === "employee" || role === "sales_rep";

  const statusList = resolveStatusList(reduxCallStatus, availableStatuses);

  const [form] = Form.useForm();
  const [mode, setMode] = useState("manual"); // 'manual' | 'excel'

  // Employee list states
  const [employeesList, setEmployeesList] = useState(propEmployees || []);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(defaultEmpId || "");

  useEffect(() => {
    if (visible) {
      if (defaultEmpId) {
        setSelectedEmployeeId(defaultEmpId);
        form.setFieldsValue({ assignedEmployeeId: defaultEmpId });
      }
      const existingDirs = form.getFieldValue("directors");
      if (!existingDirs || existingDirs.length === 0) {
        form.setFieldsValue({
          directors: [{ designation: "Director", contactName: "", contactNumber: "", directorEmailAddress: "" }],
        });
      }
    }
  }, [visible, defaultEmpId, form]);

  useEffect(() => {
    if (visible && effectiveCompanyCode) {
      if (Array.isArray(propEmployees) && propEmployees.length > 0) {
        setEmployeesList(propEmployees);
        if (!selectedEmployeeId) {
          const matched = propEmployees.find((e) => (e._id || e.id || e.employeeId) === defaultEmpId);
          const empId = matched ? matched.id || matched._id || matched.employeeId : propEmployees[0]?.id || propEmployees[0]?._id || propEmployees[0]?.employeeId || defaultEmpId;
          setSelectedEmployeeId(empId);
          form.setFieldsValue({ assignedEmployeeId: empId });
        }
      } else {
        void getEmployees(effectiveCompanyCode)
          .then((res) => {
            if (res?.success && Array.isArray(res.employees) && res.employees.length > 0) {
              setEmployeesList(res.employees);
              setSelectedEmployeeId((prev) => {
                if (prev && res.employees.some((e) => (e._id || e.id || e.employeeId) === prev)) {
                  form.setFieldsValue({ assignedEmployeeId: prev });
                  return prev;
                }
                const matched = res.employees.find((e) => (e._id || e.id || e.employeeId) === defaultEmpId);
                const resolved = matched ? matched.id || matched._id || matched.employeeId : res.employees[0]?._id || res.employees[0]?.id || res.employees[0]?.employeeId || defaultEmpId;
                form.setFieldsValue({ assignedEmployeeId: resolved });
                return resolved;
              });
            }
          })
          .catch((err) => console.error("Failed to load employees:", err));
      }
    }
  }, [visible, effectiveCompanyCode, propEmployees, defaultEmpId, form, selectedEmployeeId]);

  // Excel Import States
  const [excelFile, setExcelFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);
  const [leadSetLabel, setLeadSetLabel] = useState("Inbound lead");
  const [customSetLabel, setCustomSetLabel] = useState("");
  const [batchDefaultStatus, setBatchDefaultStatus] = useState(() => statusList[0] || "New");
  const [showAllColumns, setShowAllColumns] = useState(false);

  useEffect(() => {
    if (statusList.length > 0 && !statusList.includes(batchDefaultStatus)) {
      setBatchDefaultStatus(statusList[0]);
    }
  }, [statusList, batchDefaultStatus]);
  const [mapping, setMapping] = useState({
    companyName: "",
    contactName: "",
    contactNumber: "",
    lastName: "",
    directorEmailAddress: "",
    designation: "",
    initialRemark: "",
    mainDivisionDescription: "",
    companyDescription: "",
    cin: "",
    dateOfIncorporation: "",
    companyEmail: "",
    authorisedCapital: "",
    paidUpCapital: "",
    totalObligationOfContribution: "",
    addressType: "",
    streetAddressLine1: "",
    streetAddressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    directorDin: "",
    directorFirstName: "",
    directorLastName: "",
    directorMobileNumber: "",
    directorPermanentAddressLine1: "",
    directorPermanentAddressLine2: "",
    directorPermanentCity: "",
    directorPermanentState: "",
    directorPermanentPincode: "",
    directorPresentAddressLine1: "",
    directorPresentAddressLine2: "",
    directorPresentCity: "",
    directorPresentState: "",
    directorPresentPincode: "",
    mainDivisionNo: "",
    companyType: "",
    classOfCompany: "",
    companyCategory: "",
    companySubcategory: "",
    registrationNumber: "",
    companyOrigin: "",
    roc: "",
  });
  const [importing, setImporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFinishManual = async (values) => {
    const finalEmpId = values.assignedEmployeeId || selectedEmployeeId || defaultEmpId;
    const matchedEmp = employeesList.find((e) => (e._id || e.id || e.employeeId) === finalEmpId);
    const compName = values.companyName || values.leadCompanyName;

    let directorsList = Array.isArray(values.directors) && values.directors.length > 0 ? values.directors : [];

    if (directorsList.length === 0 && values.contactName) {
      directorsList = [
        {
          contactName: values.contactName,
          contactNumber: values.contactNumber,
          directorEmailAddress: values.directorEmailAddress,
          designation: values.designation || "Director",
        },
      ];
    }

    const validDirectors = directorsList.filter(
      (d) => Boolean(d?.contactName?.trim()) || Boolean(d?.contactNumber?.trim()),
    );

    if (validDirectors.length === 0) {
      message.error("Please provide at least one director / contact person with a valid name and number.");
      return;
    }

    const firstDir = validDirectors[0];
    const nameParts = (firstDir.contactName || "").trim().split(" ");

    const payload = {
      ...values,
      companyCode: effectiveCompanyCode,
      companyName: compName,
      leadCompanyName: compName,
      assignedEmployeeId: finalEmpId,
      assignedEmployeePhone: matchedEmp?.mobile || employee?.mobile || "",
      assignedEmployeeName: matchedEmp?.name || employee?.name || "",
      setLabel: values.leadSet || "Inbound lead",
      remarks: values.initialRemark ? [values.initialRemark] : [],
      contactName: firstDir.contactName || "",
      contactNumber: firstDir.contactNumber || "",
      directorEmailAddress: firstDir.directorEmailAddress || "",
      designation: firstDir.designation || "Director",
      directorFirstName: nameParts[0] || "",
      directorLastName: nameParts.slice(1).join(" ") || "",
      directors: validDirectors,
    };
    try {
      setSubmitting(true);
      if (onAddLead) {
        await onAddLead(payload);
      }
      form.resetFields();
      onClose();
    } catch (err) {
      console.error("Failed to add lead:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const data = new Uint8Array(buffer);
      const workbook = XLSX.read(data, { type: "array", cellDates: true });
      const firstSheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheet];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (json.length < 2) {
        message.error("Excel file is empty or missing data rows.");
        return;
      }

      const headers = json[0].map((h) => (h ? h.toString().trim() : ""));
      const rows = json.slice(1).filter((r) => r.some((cell) => cell !== null && cell !== undefined && cell !== ""));

      setExcelHeaders(headers);
      setParsedRows(rows);
      setExcelFile(file);

      // Auto-match comprehensive headers
      const tryMap = (keys) =>
        headers.find((h) => {
          const clean = h
            .toLowerCase()
            .replace(/[*_\s]/g, "")
            .trim();
          return keys.includes(clean);
        }) || "";

      const autoMap = {
        companyName: tryMap(["companyname", "company", "business", "leadcompanyname", "client", "org"]),
        contactName: tryMap(["firstname", "name", "contactname", "person", "directorfirstname"]),
        lastName: tryMap(["lastname", "surname", "directorlastname"]),
        contactNumber: tryMap(["contactnumber", "number", "phone", "mobile", "directormobilenumber", "tel"]),
        directorEmailAddress: tryMap(["email", "emailaddress", "directoremailaddress", "directoremail", "mail"]),
        designation: tryMap(["designation", "role", "title", "position"]),
        initialRemark: tryMap(["remark", "remarks", "note", "notes", "comment"]),
        mainDivisionDescription: tryMap(["maindivision", "maindivisiondescription", "division", "category"]),
        companyDescription: tryMap(["companydescription", "description", "businessactivity"]),
        cin: tryMap(["cin", "companyidentificationnumber"]),
        dateOfIncorporation: tryMap(["dateofincorporation", "incorporationdate", "doi"]),
        companyEmail: tryMap(["companyemail", "companyemailaddress"]),
        authorisedCapital: tryMap(["authorisedcapital", "authorizedcapital"]),
        paidUpCapital: tryMap(["paidupcapital"]),
        totalObligationOfContribution: tryMap(["totalobligationofcontribution", "totalobligation"]),
        addressType: tryMap(["addresstype"]),
        streetAddressLine1: tryMap(["streetaddressline1", "addressline1", "address1", "address"]),
        streetAddressLine2: tryMap(["streetaddressline2", "addressline2", "address2"]),
        city: tryMap(["city", "district"]),
        state: tryMap(["state", "province"]),
        postalCode: tryMap(["postalcode", "pincode", "zipcode", "zip"]),
        directorDin: tryMap(["directordin", "din"]),
        directorFirstName: tryMap(["directorfirstname"]),
        directorLastName: tryMap(["directorlastname"]),
        directorMobileNumber: tryMap(["directormobilenumber", "directormobile"]),
        directorPermanentAddressLine1: tryMap(["directorpermanentaddressline1", "dirpermaddressline1"]),
        directorPermanentAddressLine2: tryMap(["directorpermanentaddressline2", "dirpermaddressline2"]),
        directorPermanentCity: tryMap(["directorpermanentcity", "dirpermcity"]),
        directorPermanentState: tryMap(["directorpermanentstate", "dirpermstate"]),
        directorPermanentPincode: tryMap(["directorpermanentpincode", "dirpermpincode"]),
        directorPresentAddressLine1: tryMap(["directorpresentaddressline1", "dirpresaddressline1"]),
        directorPresentAddressLine2: tryMap(["directorpresentaddressline2", "dirpresaddressline2"]),
        directorPresentCity: tryMap(["directorpresentcity", "dirprescity"]),
        directorPresentState: tryMap(["directorpresentstate", "dirpresstate"]),
        directorPresentPincode: tryMap(["directorpresentpincode", "dirprespincode"]),
        mainDivisionNo: tryMap(["maindivisionno", "divisionno"]),
        companyType: tryMap(["companytype"]),
        classOfCompany: tryMap(["classofcompany"]),
        companyCategory: tryMap(["companycategory"]),
        companySubcategory: tryMap(["companysubcategory"]),
        registrationNumber: tryMap(["registrationnumber", "regno"]),
        companyOrigin: tryMap(["companyorigin", "origin"]),
        roc: tryMap(["roc", "roccode"]),
      };
      setMapping(autoMap);
      message.success(`Parsed ${rows.length} rows and detected ${headers.length} columns from Excel.`);
    } catch (err) {
      message.error("Failed to parse Excel file: " + err.message);
    }
  };

  const handleStartImport = () => {
    if (!mapping.companyName || !mapping.contactNumber) {
      message.error("Please map Company Name and Contact Number columns!");
      return;
    }

    const effectiveSet = leadSetLabel === "custom" ? customSetLabel.trim() : leadSetLabel;
    if (!effectiveSet) {
      message.error("Please select or enter a Set / Batch Label.");
      return;
    }

    setImporting(true);

    const parseExcelDate = (val) => {
      if (!val) return "";
      if (val instanceof Date) return val.toISOString().split("T")[0];
      const num = Number(val);
      if (Number.isNaN(num) || num < 10000) return String(val);
      const date = new Date((num - 25569) * 86400 * 1000);
      return Number.isNaN(date.getTime()) ? String(val) : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    const getIdx = (col) => (col ? excelHeaders.indexOf(col) : -1);

    const compIdx = getIdx(mapping.companyName);
    const nameIdx = getIdx(mapping.contactName);
    const lastNameIdx = getIdx(mapping.lastName);
    const phoneIdx = getIdx(mapping.contactNumber);
    const emailIdx = getIdx(mapping.directorEmailAddress);
    const desigIdx = getIdx(mapping.designation);
    const remarkIdx = getIdx(mapping.initialRemark);
    const divIdx = getIdx(mapping.mainDivisionDescription);
    const compDescIdx = getIdx(mapping.companyDescription);
    const cinIdx = getIdx(mapping.cin);
    const doiIdx = getIdx(mapping.dateOfIncorporation);
    const compEmailIdx = getIdx(mapping.companyEmail);
    const authCapIdx = getIdx(mapping.authorisedCapital);
    const paidCapIdx = getIdx(mapping.paidUpCapital);
    const totOblIdx = getIdx(mapping.totalObligationOfContribution);
    const addrTypeIdx = getIdx(mapping.addressType);
    const addrIdx = getIdx(mapping.streetAddressLine1);
    const addr2Idx = getIdx(mapping.streetAddressLine2);
    const cityIdx = getIdx(mapping.city);
    const stateIdx = getIdx(mapping.state);
    const pinIdx = getIdx(mapping.postalCode);
    const dirDinIdx = getIdx(mapping.directorDin);
    const dirFirstIdx = getIdx(mapping.directorFirstName);
    const dirLastIdx = getIdx(mapping.directorLastName);
    const dirPhoneIdx = getIdx(mapping.directorMobileNumber);
    const dirPermAddr1Idx = getIdx(mapping.directorPermanentAddressLine1);
    const dirPermAddr2Idx = getIdx(mapping.directorPermanentAddressLine2);
    const dirPermCityIdx = getIdx(mapping.directorPermanentCity);
    const dirPermStateIdx = getIdx(mapping.directorPermanentState);
    const dirPermPinIdx = getIdx(mapping.directorPermanentPincode);
    const dirPresAddr1Idx = getIdx(mapping.directorPresentAddressLine1);
    const dirPresAddr2Idx = getIdx(mapping.directorPresentAddressLine2);
    const dirPresCityIdx = getIdx(mapping.directorPresentCity);
    const dirPresStateIdx = getIdx(mapping.directorPresentState);
    const dirPresPinIdx = getIdx(mapping.directorPresentPincode);
    const mainDivNoIdx = getIdx(mapping.mainDivisionNo);
    const compTypeIdx = getIdx(mapping.companyType);
    const classCompIdx = getIdx(mapping.classOfCompany);
    const compCatIdx = getIdx(mapping.companyCategory);
    const compSubCatIdx = getIdx(mapping.companySubcategory);
    const regNoIdx = getIdx(mapping.registrationNumber);
    const compOriginIdx = getIdx(mapping.companyOrigin);
    const rocIdx = getIdx(mapping.roc);

    const getVal = (row, idx) => (idx >= 0 && row[idx] !== undefined && row[idx] !== null ? String(row[idx]).trim() : "");

    const importedLeads = [];
    const finalAssignedEmployeeId = selectedEmployeeId || defaultEmpId;
    const matchedEmp = employeesList.find((e) => (e._id || e.id || e.employeeId) === finalAssignedEmployeeId);

    if (!finalAssignedEmployeeId) {
      message.error("Please select an assigned employee for bulk leads import.");
      setImporting(false);
      return;
    }

    parsedRows.forEach((row, idx) => {
      const compName = getVal(row, compIdx) || "Imported Company";
      const contactNumber = getVal(row, phoneIdx);
      const fName = getVal(row, nameIdx);
      const lName = getVal(row, lastNameIdx);
      const contactName = (fName + " " + lName).trim() || fName || lName || `Decision Maker ${idx + 1}`;
      const email = getVal(row, emailIdx);
      const desig = getVal(row, desigIdx) || "Director";
      const remark = getVal(row, remarkIdx);

      if (contactNumber && compName) {
        importedLeads.push({
          companyCode: effectiveCompanyCode,
          assignedEmployeeId: finalAssignedEmployeeId,
          assignedEmployeePhone: matchedEmp?.mobile || employee?.mobile || "",
          assignedEmployeeName: matchedEmp?.name || employee?.name || "",
          leadCompanyName: compName,
          companyName: compName,
          contactName,
          contactNumber,
          directorEmailAddress: email,
          designation: desig,
          status: batchDefaultStatus || "New",
          setLabel: effectiveSet,
          leadSet: effectiveSet,
          remarks: remark ? [remark] : [],
          mainDivisionDescription: getVal(row, divIdx),
          companyDescription: getVal(row, compDescIdx),
          cin: getVal(row, cinIdx),
          dateOfIncorporation: doiIdx >= 0 && row[doiIdx] ? parseExcelDate(row[doiIdx]) : "",
          companyEmail: getVal(row, compEmailIdx),
          authorisedCapital: getVal(row, authCapIdx),
          paidUpCapital: getVal(row, paidCapIdx),
          totalObligationOfContribution: getVal(row, totOblIdx),
          addressType: getVal(row, addrTypeIdx),
          streetAddressLine1: getVal(row, addrIdx),
          streetAddressLine2: getVal(row, addr2Idx),
          city: getVal(row, cityIdx),
          state: getVal(row, stateIdx),
          postalCode: getVal(row, pinIdx),
          directorDin: getVal(row, dirDinIdx),
          directorFirstName: getVal(row, dirFirstIdx),
          directorLastName: getVal(row, dirLastIdx),
          directorMobileNumber: getVal(row, dirPhoneIdx),
          directorPermanentAddressLine1: getVal(row, dirPermAddr1Idx),
          directorPermanentAddressLine2: getVal(row, dirPermAddr2Idx),
          directorPermanentCity: getVal(row, dirPermCityIdx),
          directorPermanentState: getVal(row, dirPermStateIdx),
          directorPermanentPincode: getVal(row, dirPermPinIdx),
          directorPresentAddressLine1: getVal(row, dirPresAddr1Idx),
          directorPresentAddressLine2: getVal(row, dirPresAddr2Idx),
          directorPresentCity: getVal(row, dirPresCityIdx),
          directorPresentState: getVal(row, dirPresStateIdx),
          directorPresentPincode: getVal(row, dirPresPinIdx),
          mainDivisionNo: getVal(row, mainDivNoIdx),
          companyType: getVal(row, compTypeIdx),
          classOfCompany: getVal(row, classCompIdx),
          companyCategory: getVal(row, compCatIdx),
          companySubcategory: getVal(row, compSubCatIdx),
          registrationNumber: getVal(row, regNoIdx),
          companyOrigin: getVal(row, compOriginIdx),
          roc: getVal(row, rocIdx),
        });
      }
    });

    if (importedLeads.length === 0) {
      message.error("No valid leads found with Company Name and Contact Number.");
      setImporting(false);
      return;
    }

    if (onImportLeads) {
      onImportLeads(importedLeads, effectiveSet);
    } else {
      importedLeads.forEach((l) => onAddLead(l));
    }

    message.success(`Successfully mapped and scheduled ${importedLeads.length} leads!`);
    setImporting(false);
    setExcelFile(null);
    setParsedRows([]);
    onClose();
  };

  const handleModalClose = () => {
    form.resetFields();
    setExcelFile(null);
    setParsedRows([]);
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleModalClose}
      width={showAllColumns ? 900 : 780}
      centered
      style={{ top: 20, paddingBottom: 20 }}
      styles={{
        body: {
          maxHeight: "calc(84vh - 120px)",
          overflowY: "auto",
          padding: "10px 16px 20px 8px",
        },
      }}
      title={
        <Flex align="center" gap={10} style={{ paddingBottom: 4 }}>
          {mode === "manual" ? <UserAddOutlined style={{ color: "#2459d3", fontSize: 20 }} /> : <FileExcelOutlined style={{ color: "#10b981", fontSize: 20 }} />}
          <Title level={4} style={{ margin: 0 }}>
            {mode === "manual" ? "Add New Lead" : "Excel Lead Import"}
          </Title>
        </Flex>
      }
      footer={
        mode === "manual" ? (
          <Flex justify="flex-end" gap={8} style={{ paddingTop: 8 }}>
            <Button onClick={handleModalClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" loading={submitting} onClick={() => form.submit()}>
              Create Lead
            </Button>
          </Flex>
        ) : (
          <Flex justify="flex-end" gap={8} style={{ paddingTop: 8 }}>
            <Button onClick={handleModalClose} disabled={importing}>
              Cancel
            </Button>
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleStartImport} disabled={parsedRows.length === 0} loading={importing} style={{ background: "#10b981", borderColor: "#10b981", padding: "0 18px", height: 38 }}>
              Start Import ({parsedRows.length} Rows)
            </Button>
          </Flex>
        )
      }
    >
      <Flex vertical gap={20} style={{ marginTop: 10 }}>
        {/* Toggle Mode */}
        <Segmented
          value={mode}
          onChange={setMode}
          block
          options={[
            { label: "Manual Entry Form", value: "manual", icon: <UserAddOutlined /> },
            { label: "Excel Bulk Import & Column Mapping", value: "excel", icon: <FileExcelOutlined /> },
          ]}
        />

        {/* ── MANUAL ENTRY FORM ── */}
        {mode === "manual" && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinishManual}
            style={{ padding: "4px 8px" }}
            initialValues={{
              leadSet: "Inbound lead",
              status: "New",
              city: "Chennai",
              assignedEmployeeId: selectedEmployeeId,
              directors: [
                {
                  contactName: "",
                  contactNumber: "",
                  directorEmailAddress: "",
                  designation: "Director",
                },
              ],
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item label="Company Name" name="companyName" rules={[{ required: true, message: "Please enter company name" }]}>
                  <Input placeholder="e.g. Acme Technologies" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Lead Set / Category" name="leadSet">
                  <Select
                    options={[
                      { value: "Inbound lead", label: "Inbound lead" },
                      { value: "Outbound lead", label: "Outbound lead" },
                      { value: "MCA Lead", label: "MCA Lead" },
                      { value: "Network", label: "Network" },
                      { value: "Referral", label: "Referral" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Directors / Contacts List */}
            <Form.List name="directors">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} style={index > 0 ? { borderTop: "1px dashed #e2e8f0", paddingTop: 14, marginTop: 4 } : {}}>
                      {index > 0 && (
                        <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                          <Text strong style={{ fontSize: "0.82rem", color: "#64748b" }}>
                            Director #{index + 1}
                          </Text>
                          <Button
                            type="link"
                            danger
                            size="small"
                            onClick={() => remove(name)}
                            style={{ padding: 0, height: "auto" }}
                          >
                            Remove
                          </Button>
                        </Flex>
                      )}

                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            label="Contact Person Name"
                            name={[name, "contactName"]}
                            rules={[{ required: true, message: "Please enter contact name" }]}
                          >
                            <Input placeholder="e.g. Rahul Sharma" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            label="Contact Number"
                            name={[name, "contactNumber"]}
                            rules={[{ required: true, message: "Please enter contact number" }]}
                          >
                            <Input placeholder="e.g. +91 98765 43210" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            label="Email"
                            name={[name, "directorEmailAddress"]}
                          >
                            <Input placeholder="e.g. rahul@company.com" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            label="Designation / Role"
                            name={[name, "designation"]}
                            initialValue="Director"
                          >
                            <Select
                              options={[
                                { label: "Director", value: "Director" },
                                { label: "Manager", value: "Manager" },
                                { label: "Relative", value: "Relative" },
                                { label: "Friends", value: "Friends" },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  ))}

                  <Flex justify="flex-start" style={{ marginTop: 2, marginBottom: 16 }}>
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          contactName: "",
                          contactNumber: "",
                          directorEmailAddress: "",
                          designation: "Director",
                        })
                      }
                      style={{ padding: 0, height: "auto", fontSize: "0.85rem", fontWeight: 500 }}
                    >
                      Add another director
                    </Button>
                  </Flex>
                </>
              )}
            </Form.List>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item label="Initial Status" name="status">
                  <Select
                    options={statusList.map((st) => ({
                      value: st,
                      label: st,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Assign to Employee" name="assignedEmployeeId">
                  <Select
                    disabled={isEmployee}
                    placeholder="Select Employee..."
                    options={employeesList.map((emp) => ({
                      value: emp._id || emp.id || emp.employeeId,
                      label: `${emp.name} (${emp.mobile || "Employee"})`,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item label="City / Region" name="city">
                  <Input placeholder="e.g. Chennai, Bangalore" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Initial Remark / Notes" name="initialRemark">
              <Input.TextArea rows={2} placeholder="Add initial conversation or lead context..." />
            </Form.Item>
          </Form>
        )}

        {/* ── EXCEL IMPORT ── */}
        {mode === "excel" && (
          <Flex vertical gap={18}>
            {!excelFile ? (
              <Upload.Dragger
                name="file"
                accept=".xlsx, .xls, .csv"
                beforeUpload={(file) => {
                  void handleFileUpload(file);
                  return false;
                }}
                showUploadList={false}
                style={{ padding: "28px 16px", background: "#f8fafc", borderColor: "#cbd5e1", borderRadius: 12 }}
              >
                <p className="ant-upload-drag-icon">
                  <FileExcelOutlined style={{ color: "#10b981", fontSize: 36 }} />
                </p>
                <p className="ant-upload-text" style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  Click or drag Excel sheet (.xlsx, .xls, .csv) here to upload
                </p>
                <p className="ant-upload-hint" style={{ color: "#64748b", fontSize: "0.82rem" }}>
                  Upload spreadsheets containing company leads to bulk import into this directory.
                </p>
              </Upload.Dragger>
            ) : (
              <Alert
                message={`Selected File: ${excelFile.name} (${parsedRows.length} data rows found)`}
                type="success"
                showIcon
                style={{ borderRadius: 10, padding: "10px 14px" }}
                action={
                  <Button
                    size="small"
                    danger
                    onClick={() => {
                      setExcelFile(null);
                      setParsedRows([]);
                    }}
                  >
                    Replace File
                  </Button>
                }
              />
            )}

            {parsedRows.length > 0 && (
              <div style={{ padding: "2px 4px 12px 4px" }}>
                <Flex vertical gap={18} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                  <Flex justify="space-between" align="center" style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 12 }}>
                    <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
                      Map Excel Columns
                    </Title>
                    <Tag color="green" style={{ borderRadius: 6, padding: "2px 8px" }}>
                      {excelHeaders.length} Columns Detected
                    </Tag>
                  </Flex>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Set / Batch Label *
                        </Text>
                        <Select
                          value={leadSetLabel}
                          onChange={setLeadSetLabel}
                          style={{ width: "100%" }}
                          options={[
                            { value: "Inbound lead", label: "Inbound lead" },
                            { value: "Outbound lead", label: "Outbound lead" },
                            { value: "MCA Lead", label: "MCA Lead" },
                            { value: "Network", label: "Network" },
                            { value: "Referral", label: "Referral" },
                            { value: "custom", label: "Custom Label..." },
                          ]}
                        />
                        {leadSetLabel === "custom" && <Input placeholder="Enter custom set name..." value={customSetLabel} onChange={(e) => setCustomSetLabel(e.target.value)} style={{ marginTop: 4 }} />}
                      </Flex>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Batch Default Status
                        </Text>
                        <Select
                          value={batchDefaultStatus}
                          onChange={setBatchDefaultStatus}
                          style={{ width: "100%" }}
                          options={statusList.map((st) => ({
                            value: st,
                            label: st,
                          }))}
                        />
                      </Flex>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Assign to Employee *
                        </Text>
                        <Select
                          disabled={isEmployee}
                          value={selectedEmployeeId}
                          onChange={setSelectedEmployeeId}
                          placeholder="Select Employee"
                          style={{ width: "100%" }}
                          options={employeesList.map((emp) => ({
                            value: emp._id || emp.id || emp.employeeId,
                            label: `${emp.name} (${emp.mobile || "Employee"})`,
                          }))}
                        />
                      </Flex>
                    </Col>

                    <Col span={24}>
                      <Divider style={{ margin: "4px 0 8px" }} />
                    </Col>

                    <Col span={12}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Company Name *
                        </Text>
                        <Select value={mapping.companyName || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, companyName: v }))} placeholder="Select Column" style={{ width: "100%" }} options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={12}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Contact Phone Number *
                        </Text>
                        <Select value={mapping.contactNumber || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, contactNumber: v }))} placeholder="Select Column" style={{ width: "100%" }} options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={12}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Contact Person Name
                        </Text>
                        <Select value={mapping.contactName || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, contactName: v }))} placeholder="Select Column" style={{ width: "100%" }} options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={12}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Email Address
                        </Text>
                        <Select value={mapping.directorEmailAddress || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, directorEmailAddress: v }))} placeholder="Select Column (Optional)" allowClear style={{ width: "100%" }} options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={12}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Main Division / Category
                        </Text>
                        <Select value={mapping.mainDivisionDescription || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, mainDivisionDescription: v }))} placeholder="Select Column (Optional)" allowClear style={{ width: "100%" }} options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={12}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          Remarks / Notes
                        </Text>
                        <Select value={mapping.initialRemark || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, initialRemark: v }))} placeholder="Select Column (Optional)" allowClear style={{ width: "100%" }} options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={8}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          CIN
                        </Text>
                        <Select value={mapping.cin || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, cin: v }))} placeholder="CIN" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={8}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          City
                        </Text>
                        <Select value={mapping.city || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, city: v }))} placeholder="City" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    <Col span={8}>
                      <Flex vertical gap={6}>
                        <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
                          State
                        </Text>
                        <Select value={mapping.state || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, state: v }))} placeholder="State" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                      </Flex>
                    </Col>

                    {/* ── VIEW ALL COLUMN MAPPING TOGGLE BUTTON ── */}
                    <Col span={24}>
                      <Button
                        type="dashed"
                        icon={showAllColumns ? <UpOutlined /> : <DownOutlined />}
                        onClick={() => setShowAllColumns(!showAllColumns)}
                        style={{
                          width: "100%",
                          borderColor: "#6366f1",
                          color: "#4f46e5",
                          fontWeight: 600,
                          borderRadius: 8,
                          height: 40,
                          margin: "10px 0 4px",
                          background: showAllColumns ? "#eef2ff" : "#f8fafc",
                        }}
                      >
                        {showAllColumns ? "Hide Extended Column Mapping" : `View All Column Mapping (${excelHeaders.length} Detected Columns)`}
                      </Button>
                    </Col>

                    {/* ── EXTENDED ALL COLUMN MAPPINGS ── */}
                    {showAllColumns && (
                      <>
                        <Col span={24}>
                          <Divider style={{ margin: "12px 0 8px" }} />
                          <Text strong style={{ fontSize: "0.88rem", color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            Corporate & MCA Financial Details
                          </Text>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={6}>
                            <Text strong style={{ fontSize: "0.82rem", color: "#475569" }}>
                              Date Of Incorporation
                            </Text>
                            <Select value={mapping.dateOfIncorporation || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, dateOfIncorporation: v }))} placeholder="Date Of Incorporation" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Company Email</Text>
                            <Select value={mapping.companyEmail || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, companyEmail: v }))} placeholder="Company Email" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Authorised Capital</Text>
                            <Select value={mapping.authorisedCapital || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, authorisedCapital: v }))} placeholder="Authorised Capital" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>PaidUp Capital</Text>
                            <Select value={mapping.paidUpCapital || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, paidUpCapital: v }))} placeholder="PaidUp Capital" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Total Obligation</Text>
                            <Select value={mapping.totalObligationOfContribution || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, totalObligationOfContribution: v }))} placeholder="Total Obligation" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Company Type</Text>
                            <Select value={mapping.companyType || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, companyType: v }))} placeholder="Company Type" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Registration Number</Text>
                            <Select value={mapping.registrationNumber || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, registrationNumber: v }))} placeholder="Registration No" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>ROC Code</Text>
                            <Select value={mapping.roc || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, roc: v }))} placeholder="ROC" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Company Category</Text>
                            <Select value={mapping.companyCategory || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, companyCategory: v }))} placeholder="Category" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        {/* Address Fields */}
                        <Col span={24}>
                          <Divider style={{ margin: "4px 0" }} />
                          <Text strong style={{ fontSize: "0.82rem", color: "#6366f1" }}>
                            Registered Address Details
                          </Text>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Address Type</Text>
                            <Select value={mapping.addressType || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, addressType: v }))} placeholder="Address Type" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={16}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Street Address Line 1</Text>
                            <Select value={mapping.streetAddressLine1 || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, streetAddressLine1: v }))} placeholder="Address Line 1" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={16}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Street Address Line 2</Text>
                            <Select value={mapping.streetAddressLine2 || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, streetAddressLine2: v }))} placeholder="Address Line 2" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Postal / Pincode</Text>
                            <Select value={mapping.postalCode || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, postalCode: v }))} placeholder="Postal Code" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        {/* Director Details & Addresses */}
                        <Col span={24}>
                          <Divider style={{ margin: "4px 0" }} />
                          <Text strong style={{ fontSize: "0.82rem", color: "#6366f1" }}>
                            Director Information & Contact
                          </Text>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Director DIN</Text>
                            <Select value={mapping.directorDin || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, directorDin: v }))} placeholder="Director DIN" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Director First Name</Text>
                            <Select value={mapping.directorFirstName || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, directorFirstName: v }))} placeholder="Director First Name" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Director Last Name</Text>
                            <Select value={mapping.directorLastName || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, directorLastName: v }))} placeholder="Director Last Name" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Director Mobile Number</Text>
                            <Select value={mapping.directorMobileNumber || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, directorMobileNumber: v }))} placeholder="Director Mobile" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Dir. Perm. City</Text>
                            <Select value={mapping.directorPermanentCity || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, directorPermanentCity: v }))} placeholder="City" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>

                        <Col span={8}>
                          <Flex vertical gap={4}>
                            <Text style={{ fontSize: "0.78rem" }}>Dir. Perm. State</Text>
                            <Select value={mapping.directorPermanentState || undefined} onChange={(v) => setMapping((prev) => ({ ...prev, directorPermanentState: v }))} placeholder="State" allowClear options={excelHeaders.map((h) => ({ value: h, label: h }))} />
                          </Flex>
                        </Col>
                      </>
                    )}
                  </Row>
                </Flex>
              </div>
            )}
          </Flex>
        )}
      </Flex>
    </Modal>
  );
}

export default AddLeadModal;
